import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { ExternalBillingClaimStatus } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimStatus";
import { ExternalBillingUserInfoDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingUserInfoDto";
import { CreateExternalBillingClaimResponse } from "@/Common/Api/ExternalBilling/Dto/Responses/CreateExternalBillingClaimResponse";
import { ExternalBillingClaimResponse } from "@/Common/Api/ExternalBilling/Dto/Responses/ExternalBillingClaimResponse";
import { GetExternalBillingClaimsByPatientContactFacetIdResponse } from "@/Common/Api/ExternalBilling/Dto/Responses/GetExternalBillingClaimByPatientContactFacetIdResponse";
import { GetExternalBillingClaimSingleUseUriResponse } from "@/Common/Api/ExternalBilling/Dto/Responses/GetExternalBillingClaimSingleUseUriResponse";
import { GetExternalBillingClaimsResponse } from "@/Common/Api/ExternalBilling/Dto/Responses/GetExternalBillingClaimsResponse";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Guid } from "@/Common/Models/Guid";
import { PaginationSettings } from "@/Common/Models/PaginationSettings";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PatientBillingHistoryOrderType } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { TermsAndConditionsServiceNames } from "@/Common/Resources/Lookups/Enums/TermsAndConditionsServiceNames";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";

/**
 * The encounter information required for billing.
 */
interface BillingEncounter {
    /** The encounter ID. */
    Id: string;

    /** The encounter site ID. */
    SiteId: string;

    /** The employees related to the encounter. */
    EncounterRelatedEmployees: BillingEncounterEmployee[];
}

/**
 * The encounter employee information required for billing.
 */
interface BillingEncounterEmployee {
    /** The employee ID. */
    EmployeeId: string;
}

/**
 * A manager used for getting, creating and cancelling external billing claims.
 */
class ExternalBillingManager {
    /**
     * Open a new window and load a create claim page for the given encounter and billing provider.
     * @param encounter The encounter to create a claim for.
     * @param billingProvider The billing provider to create the claim.
     * @param billingUser The current billing user.
     * @param onClaimWindowClose The claim window onClose callback
     * @param billForEmployeeId The ID of the employee to bill for, if not the current employee
     */
    public static async openCreateEncounterClaimWindowAsync(
        encounter: BillingEncounter,
        billingProvider: BillingProvider,
        billingUser: ExternalBillingUserInfoDto | null,
        onClaimWindowClose: () => void = null,
        billForEmployeeId?: string,
        primaryClinicians?: string[]
    ): Promise<void> {
        const resolvedProvider = BillingProviderHelper.resolveBillingProvider(billingProvider);

        if (!resolvedProvider)
            return;

        const serviceName = ExternalBillingManager.getTermsAndConditionsServiceName(resolvedProvider);
        if (serviceName) {
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayTermsAndConditionsPopup,
                {
                    serviceName: serviceName,
                    onOk: () =>
                        ExternalBillingManager.openCreateEncounterClaimWindowAsync(
                            encounter,
                            resolvedProvider,
                            billingUser,
                            onClaimWindowClose,
                            billForEmployeeId,
                            primaryClinicians
                        )
                }
            );

            return;
        }

        if (!billingUser?.BillingAuthenticationIsValid) {
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayExternalBillingAuthentication,
                {
                    billingProvider: resolvedProvider,
                    onExternalBillingAuth: (authUser: ExternalBillingUserInfoDto) =>
                        ExternalBillingManager.openCreateEncounterClaimWindowAsync(
                            encounter,
                            resolvedProvider,
                            authUser,
                            onClaimWindowClose,
                            billForEmployeeId
                        )
                }
            );

            return;
        }

        if (this.shouldShowBillingEmployeeSelect(billForEmployeeId, resolvedProvider)) {
            const billableEncounterEmployeeIds = this.getBillableEncounterEmployeeIds(encounter, resolvedProvider);
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayExternalBillingEmployeeSelect,
                {
                    encounterEmployeeIds: billableEncounterEmployeeIds.encounterEmployeeIds,
                    otherEmployeeIds: billableEncounterEmployeeIds.encounterSiteEmployeeIds,
                    primaryClinicians: primaryClinicians,
                    onEmployeeSelect: selectedEmployeeId => ExternalBillingManager.openCreateEncounterClaimWindowAsync(
                        encounter,
                        resolvedProvider,
                        billingUser,
                        onClaimWindowClose,
                        selectedEmployeeId
                    )
                }
            );

            return;
        }

        const request: { EncounterId: string, BillForEmployeeId?: string, IsScheduledVisit?: boolean } = {
            EncounterId: encounter.Id,
            BillForEmployeeId: billForEmployeeId
        };

        if (BillingProviderHelper.getBillingProviderRequiresScheduledVisitCheck(resolvedProvider)) {
            if (IsNullOrUndefined(sessionStore.isScheduledVisitChecked)) {
                sessionStore.dispatchMain(
                    LocalOperations.MainComponentViewOperations.ShowVisitTypeSelectionPopup,
                    {
                        callback: () => ExternalBillingManager.openCreateEncounterClaimWindowAsync(
                            encounter,
                            resolvedProvider,
                            billingUser,
                            onClaimWindowClose,
                            billForEmployeeId
                        )
                    }
                );

                return;
            }

            request.IsScheduledVisit = sessionStore.isScheduledVisitChecked;
        }
        await ExternalBillingManager.createClaim(request, encounter, resolvedProvider, onClaimWindowClose);
    }

    /**
     * Open a new window and load a create claim page for the given encounter and billing provider.
     * @param renewalPrescriptionId The ID of the prescription renewal to create a claim for.
     * @param billingProvider The billing provider to create the claim.
     */
    public static async openCreatePrescriptionClaimWindowAsync(
        renewalPrescriptionId: Guid | string,
        billingProvider: BillingProvider,
        billingUser: ExternalBillingUserInfoDto | null
    ): Promise<void> {
        const resolvedProvider = BillingProviderHelper.resolveBillingProvider(billingProvider);

        if (!resolvedProvider)
            return;

        const serviceName = ExternalBillingManager.getTermsAndConditionsServiceName(resolvedProvider);
        if (serviceName) {
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayTermsAndConditionsPopup,
                {
                    serviceName: serviceName,
                    onOk: () =>
                        ExternalBillingManager.openCreatePrescriptionClaimWindowAsync(
                            renewalPrescriptionId,
                            resolvedProvider,
                            billingUser
                        )
                }
            );

            return;
        }

        if (!billingUser?.BillingAuthenticationIsValid) {
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayExternalBillingAuthentication,
                {
                    billingProvider: resolvedProvider,
                    onExternalBillingAuth: (authUser: ExternalBillingUserInfoDto) =>
                        ExternalBillingManager.openCreatePrescriptionClaimWindowAsync(
                            renewalPrescriptionId,
                            resolvedProvider,
                            authUser
                        )
                }
            );

            return;
        }

        const response = await Client.postAsync<CreateExternalBillingClaimResponse>(
            ServerOperations.Server.ExternalBilling_CreateClaimForPrescription,
            {
                RenewalPrescriptionId: renewalPrescriptionId
            }
        );

        if (response.isSuccess) {
            const claimUri = response.data.SingleUseUri.ClaimUri;
            ExternalBillingManager.reloadPrescriptionClaimsOnWindowClose(
                ExternalBillingManager.addApiAuthIfNeeded(claimUri),
                `createClaim_${EnumHelper.getName(BillingProvider, resolvedProvider)}_${renewalPrescriptionId}`
            );
        }
        else {
            await ExternalBillingManager.handleBillingError(
                response.errors,
                (authUser: ExternalBillingUserInfoDto) =>
                    ExternalBillingManager.openCreatePrescriptionClaimWindowAsync(
                        renewalPrescriptionId,
                        resolvedProvider,
                        authUser
                    )
            );
        }
    }

    /**
     * Open a new window and load the claim details page.
     * @param claim The external billing claim information to load.
     */
    public static async openClaimWindowAsync(claim: ExternalBillingClaimDto, callback: () => void = null): Promise<void> {
        const serviceName = ExternalBillingManager.getTermsAndConditionsServiceName(claim.BillingProvider);
        if (serviceName) {
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayTermsAndConditionsPopup,
                {
                    serviceName: serviceName,
                    onOk: async () => await ExternalBillingManager.getSingleClaimUseUri(claim, callback)
                }
            );
        }
        else {
            await ExternalBillingManager.getSingleClaimUseUri(claim, callback);
        }
    }

    /**
     * Get the external billing claim details.
     * @param encounterId The ID of the encounter the claim is linked to.
     * @param billingProvider The billing provider the claim was created in.
     * @param providerClaimId The provider-specific ID of the created claim.
     */
    public static async getClaimAsync(
        encounterId: string,
        billingProvider: BillingProvider,
        providerClaimId: string
    ): Promise<ExternalBillingClaimDto | null> {
        const response = await Client.postAsync<ExternalBillingClaimResponse>(
            ServerOperations.Server.ExternalBilling_GetClaim,
            {
                EncounterId: encounterId,
                BillingProvider: billingProvider,
                ProviderClaimId: providerClaimId
            }
        );

        if (!response.isSuccess) {
            await ExternalBillingManager.handleBillingError(response.errors);

            return null;
        }

        return response.data.Claim;
    }

    /**
     * Cancel an external billing claim.
     * @param encounterId The ID of the encounter the claim is linked to.
     * @param billingProvider The billing provider the claim was created in.
     * @param providerClaimId The provider-specific ID of the created claim.
     */
    public static async cancelClaimAsync(
        encounterId: string,
        billingProvider: BillingProvider,
        providerClaimId: string
    ): Promise<boolean> {
        const response = await Client.postAsync<GenericResponse>(
            ServerOperations.Server.ExternalBilling_DeleteClaim,
            {
                EncounterId: encounterId,
                BillingProvider: billingProvider,
                ProviderClaimId: providerClaimId
            }
        );

        if (!response.isSuccess) {
            await ExternalBillingManager.handleBillingError(response.errors);

            return false;
        }

        return true;
    }

    /**
     * Get all external billing claims linked to encounters.
     * @param encounterIds The IDs of the encounters to get linked claims for.
     * @param shouldFetchLatestStatusFromProvider Indicates whether the latest status should be fetched from the billing provider.
     */
    public static async getClaimsByEncounterIdsAsync(encounterIds: string[], shouldFetchLatestStatusFromProvider: boolean = false): Promise<ExternalBillingClaimDto[]> {
        const response = await Client.postAsync<GetExternalBillingClaimsResponse>(
            ServerOperations.Server.ExternalBilling_GetClaimsByEncounterIds,
            { EncounterIds: encounterIds, ShouldFetchLatestStatusFromProvider: shouldFetchLatestStatusFromProvider }
        );

        return response.isSuccess
            ? response.data.Claims
            : null;
    }

    /**
     * Get all external billing claims linked to patient.
     * @param patientContactFacetId The ID of the patient.
     * @param paginationSettings Global settings for pagination.
     * @param shouldFetchLatestStatusFromProvider Indicates whether the latest status should be fetched from the billing provider.
     */
    public static async getClaimsByPatientIdAsync(
        patientContactFacetId: string,
        paginationSettings: PaginationSettings<PatientBillingHistoryOrderType>,
        shouldFetchLatestStatusFromProvider: boolean
    ): Promise<GetExternalBillingClaimsByPatientContactFacetIdResponse> {
        const response = await Client.postAsync<GetExternalBillingClaimsByPatientContactFacetIdResponse>(
            ServerOperations.Server.ExternalBilling_GetClaimsByPatientContactFacetId,
            {
                PatientContactFacetId: patientContactFacetId,
                PaginationSettings: paginationSettings,
                ShouldFetchLatestStatusFromProvider: shouldFetchLatestStatusFromProvider
            }
        );

        return response.isSuccess
            ? response.data
            : null;
    }

    /**
     * Get whether an employee can view the details of a claim.
     * @param employee The employee.
     * @param claim The claim.
     */
    public static canEmployeeViewClaimDetails(
        employee: EmployeeUIModel,
        claim: ExternalBillingClaimDto
    ): boolean {
        const isDeprecatedBillingProvider = BillingProviderHelper.IsDeprecatedBillingProviderThatDisplaysHistoricalClaims(claim.BillingProvider);

        return !isDeprecatedBillingProvider && (
            employee.ContactFacetId === claim.CreatedByEmployeeId
            || employee.ContactFacetId === claim.CreatedForEmployeeId);
    }

    /**
     * Get the display text for a given claim.
     * @param claim The claim.
     */
    public static getDisplayText(claim: ExternalBillingClaimDto): string {
        let amount = "";
        if (this.canEmployeeViewClaimDetails(sessionStore.getCurrentEmployee(), claim)) {
            if (!IsNullOrUndefined(claim.AmountPaid))
                amount = ` - ${claim.AmountPaid.toFixed(2)}$`;

            else if (!IsNullOrUndefined(claim.AmountSubmitted))
                amount = ` - ≈${claim.AmountSubmitted.toFixed(2)}$`;
        }

        return `${claim.DisplayText}${amount}`;
    }

    /**
     * Get the label for a claim status.
     * @param status The claim status.
     */
    public static getStatusLabel(status: ExternalBillingClaimStatus): string {
        const statusName = ExternalBillingClaimStatus[status];

        if (!statusName)
            return cacheStore.GetString("UnknownLabel");

        const statusKey = `ExternalBillingClaimStatus_${statusName}`;

        return cacheStore.HasString(statusKey)
            ? cacheStore.GetString(statusKey)
            : statusName;
    }

    public static getTermsAndConditionsServiceName(billingProvider: BillingProvider): TermsAndConditionsServiceNames | null {
        switch (billingProvider) {
            case BillingProvider.ClinicAid:
                return !sessionStore.getCurrentEmployee().hasAgreedToTermsAndConditions(TermsAndConditionsServiceNames.ClinicAid) ?
                    TermsAndConditionsServiceNames.ClinicAid :
                    null;
            default:
                return null;
        }
    }

    public static getUserDisplayParts(billingEmployee: ExternalBillingUserInfoDto | null): string[] {
        if (!billingEmployee)
            return [];

        return [
            billingEmployee.BillingUsername
        ];
    }

    /**
     * Get whether we should show the employee select popup
     * @param billForEmployeeId The ID of the bill for employee ID
     * @param billingProvider The billing provider
     * @returns
     */
    private static shouldShowBillingEmployeeSelect(billForEmployeeId: string, billingProvider: BillingProvider): boolean {
        // show the pop-up if the feature is enabled, no bill for employee is selected and the current employee is not billable
        return cacheStore.ClinicSettings.EnableExternalBillingEmployeeSelectPopup
            && BillingProviderHelper.getBillingProviderSupportsEmployeeSelect(billingProvider)
            && IsNullOrUndefined(billForEmployeeId)
            && !BillingProviderHelper.getCurrentEmployeeIsBillableForProvider(billingProvider);
    }

    /**
     * Get the billable employees for the encounter.
     * @param encounter The encounter
     * @param billingProvider The billing provider
     * @returns A tuple of the billable encounter employees, and the billable employees at the encounter site.
     */
    private static getBillableEncounterEmployeeIds(encounter: BillingEncounter, billingProvider: BillingProvider): { encounterEmployeeIds: string[], encounterSiteEmployeeIds: string[] } {
        const encounterEmployeeIds = encounter.EncounterRelatedEmployees
            .map(encounterRelatedEmployee => cacheStore.GetEmployeeByContactFacetId(encounterRelatedEmployee.EmployeeId))
            .filter(encounterEmployee => BillingProviderHelper.getEmployeeIsBillableForProvider(
                billingProvider,
                encounterEmployee,
                EmployeeUIModel.fromCompositeObject(encounterEmployee).getBillingRegionIsoCode()
            ))
            .map(encounterEmployee => encounterEmployee.ContactFacet_id);

        const encounterSiteEmployeeIds = cacheStore.EmployeeCache
            .filter(employee => !encounterEmployeeIds.contains(employee.ContactFacet_id) && employee.AssociatedSites.contains(encounter.SiteId))
            .filter(encounterSiteEmployee => BillingProviderHelper.getEmployeeIsBillableForProvider(
                billingProvider,
                encounterSiteEmployee,
                EmployeeUIModel.fromCompositeObject(encounterSiteEmployee).getBillingRegionIsoCode()
            ))
            .map(billableEncounterSiteEmployee => billableEncounterSiteEmployee.ContactFacet_id);

        return {
            encounterEmployeeIds: encounterEmployeeIds,
            encounterSiteEmployeeIds: encounterSiteEmployeeIds
        };
    }

    private static reloadClaimsOnWindowClose(
        claimUri: string,
        windowId: string,
        onClaimWindowCloseCallBack: () => void = null
    ): void {
        const claimWindow = MyleHelper.OpenNewWindow(
            claimUri,
            windowId,
            window.innerWidth - 100
        );

        // reload claims when the claim window closes
        // use an interval since onClose will not be called for cross-site windows
        const claimWindowCheckInterval = window.setInterval(
            () => {
                if (!claimWindow || claimWindow.closed) {
                    if (onClaimWindowCloseCallBack) {
                        onClaimWindowCloseCallBack();
                    }

                    clearInterval(claimWindowCheckInterval);
                }
            },
            1000
        );
    }

    private static reloadPrescriptionClaimsOnWindowClose(
        claimUri: string,
        windowId: string
    ): void {
        MyleHelper.OpenNewWindow(
            claimUri,
            windowId,
            window.innerWidth - 100
        );
    }

    private static handleBillingError(
        errors: ErrorMessage[],
        onAuthSuccess?: (authUser: ExternalBillingUserInfoDto) => void
    ): Promise<void> {
        if (!errors?.any()) {
            sessionStore.showPopup(cacheStore.GetString("ErrorLabel"));
            return;
        }

        const currentUserBillingProvider = sessionStore.getCurrentEmployee().getBillingProviderResolved();
        const currentUserBillingProviderName = EnumHelper.getName(BillingProvider, currentUserBillingProvider);

        if (errors.any(p => p.Code === `${currentUserBillingProviderName}BillingAuthenticationException`)) {
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayExternalBillingAuthentication,
                {
                    billingProvider: currentUserBillingProvider,
                    onExternalBillingAuth: onAuthSuccess
                }
            );
        }
        else {
            sessionStore.showPopup(errors);
        }
    }

    private static addApiAuthIfNeeded(claimUrl: string): string {
        const url = new URL(claimUrl);

        if (url.hostname === window.location.hostname)
            apiSettings.extendUrl(url, { auth: true });

        return url.toString();
    }

    private static async getSingleClaimUseUri(claim: ExternalBillingClaimDto, callback: () => void = null): Promise<void> {
        const response = await Client.postAsync<GetExternalBillingClaimSingleUseUriResponse>(
            ServerOperations.Server.Myle_ExternalBilling_GetSingleUseClaimUri,
            {
                EncounterId: claim.EncounterId,
                BillingProvider: claim.BillingProvider,
                ProviderClaimId: claim.ProviderClaimId
            }
        );

        if (!response.isSuccess) {
            await ExternalBillingManager.handleBillingError(
                response.errors,
                () => ExternalBillingManager.openClaimWindowAsync(claim, callback)
            );

            return;
        }

        const claimSingleUseUri = response.data.SingleUseUri;

        ExternalBillingManager.reloadClaimsOnWindowClose(
            ExternalBillingManager.addApiAuthIfNeeded(claimSingleUseUri.ClaimUri),
            `viewClaim_${BillingProvider[claim.BillingProvider]}_${claim.ProviderClaimId}`,
            callback
        );
    }

    private static async createClaim(
        request: { EncounterId: string, BillForEmployeeId?: string, IsScheduledVisit?: boolean },
        encounter: BillingEncounter,
        resolvedProvider: BillingProvider,
        onClaimWindowClose: () => void = null
    ): Promise<void> {
        const response = await Client.postAsync<CreateExternalBillingClaimResponse>(
            ServerOperations.Server.ExternalBilling_CreateClaim,
            request
        );

        if (response.isSuccess) {
            const claimUri = response.data.SingleUseUri.ClaimUri;
            ExternalBillingManager.reloadClaimsOnWindowClose(
                ExternalBillingManager.addApiAuthIfNeeded(claimUri),
                `createClaim_${EnumHelper.getName(BillingProvider, resolvedProvider)}_${encounter.Id}`,
                onClaimWindowClose
            );
        }
        else {
            await ExternalBillingManager.handleBillingError(
                response.errors,
                (authUser: ExternalBillingUserInfoDto) =>
                    ExternalBillingManager.openCreateEncounterClaimWindowAsync(
                        encounter,
                        resolvedProvider,
                        authUser,
                        onClaimWindowClose,
                        request.BillForEmployeeId
                    )
            );
        }
    }
}

export { ExternalBillingManager };
